/*
 * ProfanitySound — HOW LOUD the censor sound is. One formula for the live
 * preview (profanity-censor-core.js, WebAudio) and the export (the FFmpeg gate
 * graph), so what the user hears while reviewing is what the file contains.
 *
 * Until 24.09.2026 they were two formulas: the preview drove a full-scale
 * oscillator at 0.32 × volume, the export used FFmpeg's `sine` source — whose
 * amplitude is fixed at 1/8 — times volume. Measured on the exported file at
 * the default 50 %: peak −24.1 dBFS (RMS −27.1) against −15.9 dBFS in the
 * preview — the file's beep was 8.2 dB quieter than the one the user had
 * approved by ear, i.e. barely audible under ordinary speech. /audio-censor
 * found the same `sine` 1/8 trap on 09.07 and fixed it with one param table;
 * this is that lesson applied here.
 *
 * Auto level (the /audio-censor default, «нет ни у одного конкурента»): the
 * beep sits AUTO_OFFSET_DB under the RMS of the speech around the censor
 * (±AUTO_CTX_S, the censored span itself excluded — it may be the shout),
 * the broadcast convention. A quiet podcast gets a quiet beep, a loud stream a
 * loud one; the manual slider stays for those who want one level everywhere.
 */
(function (root) {
    'use strict';

    var P = {
        // Preview peak = PREVIEW_GAIN × volume (a full-scale oscillator):
        // the level users have been approving by ear since June — kept.
        PREVIEW_GAIN: 0.32,
        // FFmpeg `sine` source amplitude (measured: −18.06 dBFS peak).
        FFMPEG_SINE_AMP: 1 / 8,
        AUTO_OFFSET_DB: -10,
        AUTO_CTX_S: 1.0,
        AUTO_MIN_PEAK: 0.02,
        AUTO_MAX_PEAK: 0.9
    };

    /* Peak amplitude (0..1) of the beep inside region r.
     *   s = { volume: 0.1..1, auto: bool }
     *   audio = ProfanityRegions.audio(pcm) or null (then: manual level) */
    function beepPeak(audio, r, s) {
        var manual = P.PREVIEW_GAIN * Math.max(0, Math.min(1, s.volume));
        if (!s.auto || !audio || !audio.contextRms || !r) return manual;
        var ctxRms = audio.contextRms(r.start, r.end, P.AUTO_CTX_S);
        if (!(ctxRms > 0)) return manual;
        var peak = ctxRms * Math.pow(10, P.AUTO_OFFSET_DB / 20) * Math.SQRT2;
        return Math.max(P.AUTO_MIN_PEAK, Math.min(P.AUTO_MAX_PEAK, peak));
    }

    /* The `volume` factor for FFmpeg's sine source that yields `peak`. */
    function ffmpegSineGain(peak) { return peak / P.FFMPEG_SINE_AMP; }

    /* The user's replacement sound inside one censor — ONE shape for the
     * preview (preview-gate-worklet.js) and the file (the underlay built by
     * the core), so the leaf you hear is the leaf you get.
     *
     * Until 09.10.2026 the sound LOOPED to fill the censor: in the file every
     * censor restarted it and repeated it to the end (`aloop`), in the preview
     * it ran as one endless loop since ▶ and the censor opened on whatever
     * phase it had reached. Ангелина's sound is one short rustle of a leaf:
     * «один листочек у меня, не три листочка и не пять» (chat 640). So it
     * plays ONCE from the start of the censor; repeating is an explicit
     * choice («Повторять», for a texture such as a hiss).
     *
     *   d         — seconds since the censor began
     *   regionS   — the censor's length
     *   soundS    — the sound's length
     *   loop      — repeat to fill the censor
     * Returns the envelope gain (0 when silent): a 5 ms fade-in so the start
     * does not click, a 20 ms fade-out where the sound or the censor ends. */
    var FADE_IN = 0.005, FADE_OUT = 0.02;
    function replacementEnvelope(d, regionS, soundS, loop) {
        if (!(d >= 0) || !(soundS > 0)) return 0;
        var stop = loop ? regionS : Math.min(regionS, soundS);
        if (d > stop) return 0;
        var g = 1;
        if (d < FADE_IN) g = d / FADE_IN;
        var left = stop - d;
        if (left < FADE_OUT) g = Math.min(g, left / FADE_OUT);
        return g < 0 ? 0 : g;
    }
    /* Which second of the sound plays at d (the loop wraps). */
    function replacementOffset(d, soundS, loop) {
        if (!(soundS > 0)) return 0;
        return loop ? d % soundS : d;
    }

    var api = { P: P, beepPeak: beepPeak, ffmpegSineGain: ffmpegSineGain,
        replacementEnvelope: replacementEnvelope, replacementOffset: replacementOffset,
        REPLACEMENT_FADE_IN: FADE_IN, REPLACEMENT_FADE_OUT: FADE_OUT };
    root.ProfanitySound = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    // globalThis first: the preview's AudioWorklet scope has neither `self`
    // nor a module-level `this`, and loads this file next to its gate.
})(typeof globalThis !== 'undefined' ? globalThis : (typeof self !== 'undefined' ? self : this));
